import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SectionHeader } from "./Tokenomics";

const ROADMAP = [
  {
    quarter: "Q2 2026",
    title: "Foundations",
    status: "live",
    items: [
      "Token + Presale + Staking contracts deployed (Base Sepolia)",
      "Audits commissioned — Solid Proof + Coinsult",
      "fanzcoin.fanz.website launch",
      "Presale Phase 1 opens",
    ],
  },
  {
    quarter: "Q3 2026",
    title: "TGE + DEX",
    status: "next",
    items: [
      "Mainnet deploy on Base",
      "Token Generation Event + claim window",
      "Aerodrome / Uniswap LP creation, locked",
      "First quarterly creator airdrop",
    ],
  },
  {
    quarter: "Q4 2026",
    title: "Money Plane Live",
    status: "upcoming",
    items: [
      "FanzCard universal product launches",
      "FanzMoney processor integration across all 25 platforms",
      "Per-platform staking boosts via FANZ holdings",
      "First DAO governance vote",
    ],
  },
  {
    quarter: "Q1 2027",
    title: "Galaxy Brain",
    status: "upcoming",
    items: [
      "Mobile app (iOS + TWA Android) ships with native FANZ wallet",
      "Cross-chain bridge to Arbitrum + Optimism",
      "Real-yield revenue share to stakers",
      "Tier-2 CEX listings",
    ],
  },
];

export default function Roadmap() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const lineHeight = useTransform(scrollYProgress, [0.05, 0.85], ["0%", "100%"]);

  return (
    <section id="roadmap" ref={sectionRef} className="relative mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28">
      <SectionHeader
        eyebrow="Roadmap"
        title="A creator economy in four moves."
        sub="Honest milestones, no buzzwords. Every shipped quarter is shippable.
             Every upcoming quarter is staffed."
      />

      <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_auto_1fr]">
        {/* Mobile: stacked */}
        <div className="contents">
          {ROADMAP.map((q, i) => {
            const left = i % 2 === 0;
            return (
              <MilestoneRow key={q.quarter} q={q} idx={i} left={left} />
            );
          })}
        </div>
      </div>

      {/* Center spine — desktop only — uses a real motion line */}
      <div className="pointer-events-none absolute inset-y-0 left-1/2 hidden w-px -translate-x-1/2 lg:block">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.06] to-transparent" />
        <motion.div
          style={{ height: lineHeight }}
          className="absolute left-0 top-0 w-px bg-gradient-to-b from-accent via-magenta to-cyan"
        />
      </div>
    </section>
  );
}

function MilestoneRow({
  q,
  idx,
  left,
}: {
  q: (typeof ROADMAP)[number];
  idx: number;
  left: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, 0]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [0, 1]);
  const scale = useTransform(scrollYProgress, [0, 0.6], [0.92, 1]);

  const statusColor =
    q.status === "live" ? "#22c55e" : q.status === "next" ? "#c084fc" : "#9ca3af";

  return (
    <>
      {/* desktop: alternating columns; mobile: single col */}
      <motion.div
        ref={ref}
        style={{ y, opacity, scale }}
        className={`order-${idx + 1} lg:order-none ${
          left ? "lg:col-start-1" : "lg:col-start-3"
        }`}
      >
        <div className="panel panel-hover relative p-6 md:p-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em]">
                <span className="font-mono text-white/60">{q.quarter}</span>
                <span
                  className="rounded-full px-2 py-0.5 text-[10px]"
                  style={{
                    background: `${statusColor}1f`,
                    color: statusColor,
                    border: `1px solid ${statusColor}50`,
                  }}
                >
                  {q.status}
                </span>
              </div>
              <h3 className="mt-2 font-display text-3xl text-white">{q.title}</h3>
            </div>
            <div className="font-display text-5xl text-white/[0.07]">0{idx + 1}</div>
          </div>

          <ul className="mt-5 space-y-2.5">
            {q.items.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-gray-300">
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: statusColor, boxShadow: `0 0 8px ${statusColor}` }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </motion.div>

      {/* desktop: spacer column */}
      <div className="hidden lg:block lg:col-start-2 lg:row-span-1">
        <div className="relative h-full">
          <div className="absolute left-1/2 top-12 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full border border-white/10 bg-background">
            <div
              className="h-2 w-2 rounded-full"
              style={{ background: statusColor, boxShadow: `0 0 12px ${statusColor}` }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
